import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { createTouchOpenAutoFocusHandler } from "@/lib/touchAutoFocus";
import {
  SheetCloseButton,
  SheetGrabZone,
  SHEET_ANIM_RISE,
  SHEET_ANIM_CENTERED,
  SHEET_CENTERED_PHONE_CLASS,
  SHEET_GRAB_ZONE_CLASS,
  SHEET_MAX_H_CLASS,
  SHEET_OVERLAY_CLASS,
  SHEET_RADIUS_TOP_CLASS,
  type SheetFit,
} from "@/components/ui/sheetChrome";
import { useSheetFitClass } from "@/hooks/use-sheet-fit";
import { VEILED_CLASS } from "@/components/ui/dialogVeil";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * ProtectedDialogContent
 *
 * Drop-in replacement for `DialogContent` for data-entry / wizard dialogs.
 *
 * Behaviour vs the standard DialogContent:
 *  - Tapping outside the card does NOT close the dialog (no data loss).
 *  - Pressing Escape does NOT close the dialog.
 *  - Clicking the X button shows a confirmation: "¿Descartar cambios?".
 *
 * Same visual layout/classes as the original DialogContent.
 *
 * IMPORTANT: This is a purely UX/visual safeguard — it does not change
 * any submit, mutation, validation, or business logic of the parent
 * dialog. The host component is still in charge of `onOpenChange`
 * (which is only fired AFTER the user confirms discard, or normally
 * via the host's own Save/Cancel buttons).
 */

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      SHEET_OVERLAY_CLASS,
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "ProtectedDialogOverlay";

interface ProtectedDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Hide the X close button entirely (rare). */
  hideCloseButton?: boolean;
  /** Oculta el tirador (para carcasas que pintan el suyo propio). */
  hideHandle?: boolean;
  /**
   * Called when the user confirms discarding changes via the discard dialog.
   * If not provided, the host's `onOpenChange(false)` (the Radix default close
   * via the X button) will be invoked. We do this by clicking a synthetic
   * <DialogClose> element after confirmation.
   */
  onDiscard?: () => void;
  /**
   * Whether the dialog has unsaved user input. When false, clicking X closes
   * immediately without confirmation. Defaults to true (legacy behavior).
   */
  isDirty?: boolean;
  /**
   * Cuánto ocupa la hoja en el teléfono (ver `SheetFit`). Aquí el que se
   * desplaza es el PANEL, así que se mide él mismo.
   *
   * Las carcasas que van montadas encima y llevan su propio cuerpo (FormSheet,
   * TaskFormSheetShell) pasan `short`: el alto lo deciden ellas y esta no debe
   * volver a opinar.
   */
  fit?: SheetFit;
  /**
   * Apartar la hoja mientras hay otra ENCIMA — ver `dialogVeil`. Se esconde el
   * panel Y su velo: si el velo se quedara, cada ventana encadenada oscurecería
   * la pantalla un poco más.
   */
  veiled?: boolean;
  /**
   * Ventana CENTRADA también en el teléfono, con aire alrededor y sin tirador
   * (27-09-2026, `SHEET_CENTERED_PHONE_CLASS`). Sin la prop, lo de siempre: hoja
   * inferior en el teléfono y ventana centrada de tableta en adelante.
   */
  centered?: boolean;
}

export const ProtectedDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ProtectedDialogContentProps
>(({ className, children, hideCloseButton, hideHandle, onDiscard, isDirty, fit = "auto", veiled, centered = false, onOpenAutoFocus, ...props }, ref) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [autoDirty, setAutoDirty] = React.useState(false);
  const closeRef = React.useRef<HTMLButtonElement>(null);

  // El panel ES el que hace scroll aquí, así que se mide a sí mismo. Hay que
  // quedarse con el nodo sin robarle la `ref` a quien la haya pasado.
  const [panel, setPanelNode] = React.useState<HTMLDivElement | null>(null);
  const setPanel = React.useCallback(
    (node: HTMLDivElement | null) => {
      setPanelNode(node);
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );
  /* `open` es opcional: si el consumidor no lo pasa, se da por abierta — este
     componente solo se monta dentro de un Dialog ya abierto (Radix desmonta el
     contenido al cerrar), así que es cierto salvo en las carcasas que fuerzan el
     montaje. Con `fit="short"` no se mide nada y da igual. */
  // Centrada = ventana flotante, no hoja: crecer no significa nada ahí.
  const fitClass = useSheetFitClass({ fit: centered ? "short" : fit, host: panel });

  // Compose the final dirty state: explicit prop wins when provided,
  // otherwise fall back to auto-detection from native input/change events.
  const effectiveDirty = isDirty ?? autoDirty;

  const performClose = () => {
    if (onDiscard) {
      onDiscard();
    } else {
      setTimeout(() => closeRef.current?.click(), 0);
    }
  };

  const handleConfirmDiscard = () => {
    setConfirmOpen(false);
    performClose();
  };

  const handleXClick = () => {
    if (effectiveDirty) {
      setConfirmOpen(true);
    } else {
      performClose();
    }
  };

  // Auto-detect any user input inside the dialog subtree.
  const markDirty = React.useCallback(() => {
    setAutoDirty(true);
  }, []);

  return (
    <>
      <DialogPortal>
        <DialogOverlay className={cn(veiled && VEILED_CLASS)} />
        <DialogPrimitive.Content
          ref={setPanel}
          // Block accidental dismissals — only the X (with confirmation) closes.
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onOpenAutoFocus={createTouchOpenAutoFocusHandler(onOpenAutoFocus)}
          onInput={markDirty}
          onChange={markDirty}
          className={cn(
            "fixed z-50 grid gap-4 border bg-primary-foreground p-6 shadow-lg",
            "overflow-y-auto overflow-x-hidden overscroll-contain",
            centered
              ? // Centrada en TODOS los tamaños, con aire alrededor (ver la constante).
                cn(SHEET_CENTERED_PHONE_CLASS, SHEET_ANIM_CENTERED)
              : cn(
                  // Teléfono: hoja inferior anclada abajo, a todo el ancho. Antes era
                  // una tarjeta centrada que aparecía haciendo zoom en medio de la
                  // pantalla, y cada carcasa se la volvía a colocar abajo a base de
                  // `!left-0 !bottom-0 !translate-x-0 …`.
                  "inset-x-0 bottom-0 w-full border-t pt-7 pb-[max(1.5rem,var(--safe-bottom))]",
                  SHEET_MAX_H_CLASS,
                  SHEET_RADIUS_TOP_CLASS,
                  SHEET_ANIM_RISE,
                  // Tableta y escritorio: la ventana centrada de siempre. Los
                  // desplazamientos del zoom ya vienen en SHEET_ANIM_RISE con los
                  // mismos -50%/-48% que este translate.
                  "md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
                  "md:w-[calc(100vw-2rem)] md:max-w-lg md:max-h-[calc(100vh-4rem)] md:border md:p-6",
                  // Literal a propósito: Tailwind lee el código fuente, así que una
                  // clase construida en tiempo de ejecución no se generaría.
                  "md:rounded-[24px]",
                ),
            className,
            /* El velo va ANTES de `fitClass` a propósito: no comparte grupo de
               tailwind-merge con ninguna altura, así que el orden le da igual, y
               el guardián de alturas exige que detrás de `fitClass` solo venga
               el cierre del `cn()`. */
            veiled && VEILED_CLASS,
            // AL FINAL, después del `className` del consumidor: mismo grupo de
            // tailwind-merge, gana la última. Ver `useSheetFitClass`.
            fitClass,
          )}
          style={{ touchAction: "pan-y" }}
          {...props}
        >
          {/* El arrastre pasa por handleXClick, el MISMO camino que la X: un
              formulario con cambios pregunta antes de descartarlos. */}
          {/* Centrada no lleva tirador: el arrastre escribe un translateY en línea
              que pisaría el translate del centrado (como en GlassSheet). */}
          {!hideHandle && !centered && <SheetGrabZone onClose={handleXClick} className={SHEET_GRAB_ZONE_CLASS} />}
          {children}

          {/* Hidden DialogClose used as the actual closer after confirmation. */}
          <DialogPrimitive.Close ref={closeRef} className="hidden" aria-hidden tabIndex={-1} />

          {!hideCloseButton && (
            <SheetCloseButton
              onClick={handleXClick}
              aria-label={t("common.close")}
              className="absolute right-[18px] top-4"
            />
          )}
        </DialogPrimitive.Content>
      </DialogPortal>


      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="md:max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.discardChangesTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.discardChangesDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.discardConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});
ProtectedDialogContent.displayName = "ProtectedDialogContent";
